import MapFormDetail from 'forms/Map/MapFormDetail'
import useFetch from 'hooks/useFetch'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { ImSpinner9 } from 'react-icons/im'
import { LuPrinter } from 'react-icons/lu'
import { useReactToPrint } from 'react-to-print'
import { useLocation } from 'wouter'

export default function MapViewDetail({ params }) {
  const [, setLocation] = useLocation()

  // useFetch(): hook personalizado para realizar solicitudes GET, POST, PUT, DELETE
  const [fetchedIncidents, fetchIncdents] = useFetch()

  // useState()
  const [incidents, setIncidents] = useState(null)
  const [filteredIncidents, setFilteredIncidents] = useState(null)
  const [positionOptions, setPositionOptions] = useState(null)

  // ── ESTADO MAPA DE CALOR ──
  const [showHeatmap, setShowHeatmap] = useState(false)

  // Variables:
  const currentYear = new Date().getFullYear()
  const occurrenceDateStart = new Date(Date.UTC(currentYear, 0, 1))
  const occurrenceDateEnd = new Date(Date.UTC(currentYear, 11, 31))
  const [fields, setFields] = useState({
    general_search: '',
    position: '',
    occurrence_date_start: `${occurrenceDateStart.getFullYear()}-${(occurrenceDateStart.getMonth() + 1).toString().padStart(2, '0')}-${occurrenceDateStart.getDate().toString().padStart(2, '0')}`,
    occurrence_date_end: `${occurrenceDateEnd.getFullYear()}-${(occurrenceDateEnd.getMonth() + 1).toString().padStart(2, '0')}-${occurrenceDateEnd.getDate().toString().padStart(2, '0')}`,
    status: ''
  })

  // Información necesária al usar 'react-to-print':
  const incidentRef = useRef()
  const mapRef = useRef()
  const handlePrint = useReactToPrint({
    contentRef: incidentRef,
    documentTitle: `Mapa_Siniestros_TUS_${params.type === 'circulation' ? 'Circulacion' : 'Lesionados'}_${fields.incident_number}`
  })

  // CRUD: GET
  useEffect(() => {
    setIncidents(null)
    setFilteredIncidents(null)
    setShowHeatmap(false) // Resetear al cambiar de tipo de mapa

    const incidentsApiCall = {
      url: `incidents/${params.type}`,
      method: 'GET',
      messageKo: '¡Error al recoger Roles!'
    }
    fetchIncdents(incidentsApiCall)
  }, [params.type])

  // Escuchar GET
  useEffect(() => {
    if (fetchedIncidents?.data) {
      setIncidents(fetchedIncidents?.data)
      setFilteredIncidents(
        fetchedIncidents?.data?.filter((incident) => {
          let incidentDate = null
          let filterStartDate = null
          let filterEndDate = null

          // Parseamos fecha del incidente
          if (
            typeof incident.occurrence_date === 'string' &&
            incident.occurrence_date !== '' &&
            incident.occurrence_date !== null &&
            incident.occurrence_date !== undefined
          ) {
            const parts = incident.occurrence_date.split('/')

            if (parts.length === 3) {
              let [day, month, year] = parts.map((num) => parseInt(num, 10))
              year = year < 100 ? 2000 + year : year
              incidentDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0))
            }
          }

          // Parseamos fecha inicio filtro
          if (
            typeof fields.occurrence_date_start === 'string' &&
            fields.occurrence_date_start !== '' &&
            fields.occurrence_date_start !== null &&
            fields.occurrence_date_start !== undefined
          ) {
            const parts = fields.occurrence_date_start.split('-')

            if (parts.length === 3) {
              let [year, month, day] = parts.map((num) => parseInt(num, 10))
              year = year < 100 ? 2000 + year : year
              filterStartDate = new Date(Date.UTC(year, month - 1, day))
            }
          }

          // Parseamos fecha fin filtro
          if (
            typeof fields.occurrence_date_end === 'string' &&
            fields.occurrence_date_end !== '' &&
            fields.occurrence_date_end !== null &&
            fields.occurrence_date_end !== undefined
          ) {
            const parts = fields.occurrence_date_end.split('-')

            if (parts.length === 3) {
              let [year, month, day] = parts.map((num) => parseInt(num, 10))
              year = year < 100 ? 2000 + year : year
              filterEndDate = new Date(Date.UTC(year, month - 1, day))
            }
          }

          // Validamos si el incidente está dentro del rango de fechas
          const isWithinDateRange =
            (!filterStartDate || filterStartDate <= incidentDate) &&
            (!filterEndDate || filterEndDate >= incidentDate)

          // Validamos si el incidente tiene coordenadas
          const hasCoordinates =
            incident?.coordinates !== null && incident?.coordinates !== ''

          return isWithinDateRange && hasCoordinates
        })
      )

      // Si estamos en el mapa de circulación también filtramos por posición
      if (params.type === 'circulation') {
        // Filtramos solo incidentes que tengan coordenadas definidas
        const incidentsWithCoordinates =
          fetchedIncidents?.data.filter(
            (incident) =>
              incident?.coordinates !== null && incident?.coordinates !== ''
          ) || []

        // Extraemos posiciones únicas solo de esos incidentes
        const uniquePositions = [
          ...new Set(
            incidentsWithCoordinates.map((incident) => incident.position)
          )
        ]

        // Construimos las opciones para el select
        const sortedPositions = []
        sortedPositions.push({ value: '', label: '-' })
        sortedPositions.push(
          ...uniquePositions.map((position) => ({
            value: position ?? null,
            label: position ?? 'Sin definir'
          }))
        )

        setPositionOptions(sortedPositions)
      }
    } else {
      if (
        fetchedIncidents?.isLoading === false &&
        fetchedIncidents?.isCalled === true
      ) {
        setIncidents([])
        setFilteredIncidents([])
        setPositionOptions([])
      }
    }

    if (fetchedIncidents.error) {
      setLocation('/page-not-found')
    }
  }, [fetchedIncidents?.data, fetchedIncidents.error])

  const handleFilter = (values) => {
    setShowHeatmap(false) // Resetear mapa de calor al filtrar

    if (
      !values.general_search &&
      !values.position &&
      !values.occurrence_date_start &&
      !values.occurrence_date_end
    ) {
      setFilteredIncidents(
        incidents?.filter(
          (incident) =>
            incident?.coordinates !== null && incident?.coordinates !== ''
        )
      )
      return
    }

    setFilteredIncidents(
      incidents.filter((incident) => {
        // --------------------
        // Filtro general
        // --------------------

        // Convertimos el texto a minúsculas para una búsqueda sin distinguir mayúsculas
        const searchValue = values.general_search?.toLowerCase() || ''

        // Aplica la búsqueda general solo en campos específicos
        const searchableFields = [
          'incident_number',
          'agency_reference',
          'tus_reference',
          'incident_risk',
          'status'
        ]
        const matchesGeneralSearch = searchableFields.some((key) => {
          return (
            incident[key] &&
            incident[key].toString().toLowerCase().includes(searchValue)
          )
        })

        // --------------------
        // Filtro posición (Solo lo usamos cuando estamos en el mapa de circulación)
        // --------------------

        // Validamos si la posición del incidente coincide con el valor del filtro
        let matchesPosition = true
        if (params.type === 'circulation' && values.position !== '') {
          matchesPosition =
            incident.position?.toString() === values.position?.toString()
        }

        // --------------------
        // Filtro fechas
        // --------------------

        let incidentDate = null
        let filterStartDate = null
        let filterEndDate = null

        // Parseamos fecha del incidente
        if (
          typeof incident.occurrence_date === 'string' &&
          incident.occurrence_date !== '' &&
          incident.occurrence_date !== null &&
          incident.occurrence_date !== undefined
        ) {
          const parts = incident.occurrence_date.split('/')

          if (parts.length === 3) {
            let [day, month, year] = parts.map((num) => parseInt(num, 10))
            year = year < 100 ? 2000 + year : year
            incidentDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0))
          }
        }

        // Parseamos fecha inicio filtro
        if (
          typeof values.occurrence_date_start === 'string' &&
          values.occurrence_date_start !== '' &&
          values.occurrence_date_start !== null &&
          values.occurrence_date_start !== undefined
        ) {
          const parts = values.occurrence_date_start.split('-')

          if (parts.length === 3) {
            let [year, month, day] = parts.map((num) => parseInt(num, 10))
            year = year < 100 ? 2000 + year : year
            filterStartDate = new Date(Date.UTC(year, month - 1, day))
          }
        }

        // Parseamos fecha fin filtro
        if (
          typeof values.occurrence_date_end === 'string' &&
          values.occurrence_date_end !== '' &&
          values.occurrence_date_end !== null &&
          values.occurrence_date_end !== undefined
        ) {
          const parts = values.occurrence_date_end.split('-')

          if (parts.length === 3) {
            let [year, month, day] = parts.map((num) => parseInt(num, 10))
            year = year < 100 ? 2000 + year : year
            filterEndDate = new Date(Date.UTC(year, month - 1, day))
          }
        }

        // Validamos si el incidente está dentro del rango de fechas
        const isWithinDateRange =
          (!filterStartDate || filterStartDate <= incidentDate) &&
          (!filterEndDate || filterEndDate >= incidentDate)

        // Validamos si el incidente tiene coordenadas
        const hasCoordinates =
          incident?.coordinates !== null && incident?.coordinates !== ''

        if (params.type === 'circulation') {
          return (
            matchesGeneralSearch &&
            matchesPosition &&
            isWithinDateRange &&
            hasCoordinates
          )
        } else {
          return matchesGeneralSearch && isWithinDateRange && hasCoordinates
        }
      })
    )
  }

  return filteredIncidents !== null ? (
    <>
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex items-center gap-x-3">
          <h2 className="text-lg font-medium text-gray-800 dark:text-white">
            {(() => {
              switch (params.type) {
                case 'circulation':
                  return 'Mapa Siniestros TUS - Circulación'
                case 'injured':
                  return 'Mapa Siniestros TUS - Lesionados'
                default:
                  return 'Mapa Siniestros TUS'
              }
            })()}
          </h2>

          <span className="px-3 py-1 text-xs text-white bg-[#17495E] rounded-full dark:bg-gray-800 dark:text-blue-400">
            {incidents?.length} registros
          </span>
        </div>

        <div className="flex items-center mt-4 sm:mt-0 gap-x-4">
          <button
            type="button"
            className="flex items-center justify-center w-1/2 px-5 py-2 text-sm text-white capitalize transition-colors duration-200 border rounded-sm bg-accent border-accent sm:w-36 gap-x-2 enabled:hover:bg-white enabled:hover:text-accent dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-20 focus:outline-none focus:ring focus:ring-accent focus:border-accent dark:focus:border-accent"
            onClick={() => {
              // Ajusta dinámicamente el tamaño del mapa antes y después de imprimir, ya que no se adapta correctamente a la pantalla de impresión.
              // Se redimensiona al iniciar la impresión y se restablece al finalizar.
              if (mapRef?.current) {
                const handleBeforePrint = (mapRef2) => {
                  mapRef2.current._container.classList.remove('w-full')
                  mapRef2.current._container.style.width = '800px'
                  mapRef2.current._container.style.height = '600px'
                  mapRef2.current.invalidateSize()
                }

                const handleAfterPrint = (mapRef2) => {
                  mapRef2.current._container.style.removeProperty('width')
                  mapRef2.current._container.classList.add('w-full')
                  mapRef2.current._container.style.height = '600px'
                  mapRef2.current.invalidateSize()
                }

                window.onbeforeprint = handleBeforePrint(mapRef)

                setTimeout(function () {
                  window.onafterprint = handleAfterPrint(mapRef)
                }, 10)
              }

              handlePrint()
            }}
          >
            <LuPrinter size={20} />
            <span>Imprimir</span>
          </button>
        </div>
      </div>

      {/* Renderiza el componente MapFormDetail con los datos del mapa */}
      <MapFormDetail
        type={params.type}
        fields={fields}
        filteredIncidents={filteredIncidents}
        positionOptions={positionOptions}
        incidentRef={incidentRef}
        mapRef={mapRef}
        handleFilter={handleFilter}
        showHeatmap={showHeatmap}
        onToggleHeatmap={() => setShowHeatmap((prev) => !prev)}
      />
    </>
  ) : (
    <div className="flex items-center content-center justify-center w-full h-full">
      <p
        className="flex flex-col-reverse items-center justify-center h-full font-bold dark:text-[#F5F5F5] text-gray-700"
        title="Cargando..."
      >
        <ImSpinner9 size={70} className="animate-spin" />
      </p>
    </div>
  )
}

MapViewDetail.propTypes = {
  params: PropTypes.any
}